/**
 * Size Selector Modal Component
 * Custom modal for selecting and creating custom T-shirt sizes
 * Uses vanilla JavaScript and Web Components pattern
 *
 * GENDER-SPECIFIC BODY TYPE HANDLING:
 * -------------------------------------
 * The backend API (customer/v2/measurement) currently only has a single 'bodyType' field
 * which was designed for male customers. To support female customers without backend changes,
 * we use a composite format:
 *
 * Male Format (Simple):
 *   bodyType: "Light Belly"
 *   bodyType: "Heavy Belly"
 *   bodyType: "No Belly"
 *
 * Female Format (Composite):
 *   bodyType: "Curvy Hips|Average"
 *   bodyType: "Straight|Petite"
 *   bodyType: "Hourglass|Full"
 *   Format: "silhouetteShape|bustProfile"
 *
 * The composite format is:
 * 1. Created in saveMeasurement() when saving female measurements
 * 2. Stored in the backend's bodyType field as a single string
 * 3. Parsed in mapCustomerData() when loading saved measurements
 * 4. Split back into silhouetteShape and bustProfile for display
 *
 * This approach allows full female support without requiring:
 * - Backend API changes
 * - Database schema migrations
 * - DTO field additions
 *
 * When the backend eventually adds silhouetteShape and bustProfile fields,
 * this implementation can be updated to use separate fields instead.
 */

class SizeSelectorModal extends HTMLElement {
  constructor() {
    super();

    // State management
    this.state = {
      currentSection: 'section-1', // section-1, section-2, section-3, section-4, section-5
      currentTab: 'im-new', // 'im-new' or 'shopped-before'
      selectedSize: 'M',
      selectedMeasurement: '40',
      selectedBodyType: 'Light Belly',
      selectedShape: 'Curvy Hips',
      selectedBust: 'Average',
      selectedHeight: '5.6',
      selectedFit: 'fitted',
      measurements: {
        chestWidth: 100,
        shoulderWidth: 35,
        cbLength: 90,
        sleeveLength: 60
      },
      unit: 'cm', // 'cm' or 'inch'
      gender: 'male', // Will be set from Liquid variable
      customerId: null,
      savedSizes: [],
      selectedSavedSize: null,
      // Edit mode state
      isEditMode: false,
      editingSizeId: null,
      editingSizeName: null,
      editingOriginalSize: null
    };

    // Bind methods
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.goToSection = this.goToSection.bind(this);
    this.handleReturn = this.handleReturn.bind(this);
    this.switchTab = this.switchTab.bind(this);
    this.loadSavedSizes = this.loadSavedSizes.bind(this);
    this.handleSizeSelect = this.handleSizeSelect.bind(this);
    this.saveMeasurement = this.saveMeasurement.bind(this);
    this.handleMeasurementDetails = this.handleMeasurementDetails.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    // Edit mode methods
    this.enterEditMode = this.enterEditMode.bind(this);
    this.exitEditMode = this.exitEditMode.bind(this);
    this.populateFormWithSavedSize = this.populateFormWithSavedSize.bind(this);
    this.updateMeasurement = this.updateMeasurement.bind(this);
    // Measurement slider methods
    this.storeOriginalMeasurements = this.storeOriginalMeasurements.bind(this);
    this.populateMeasurementSliders = this.populateMeasurementSliders.bind(this);
    this.updateMeasurementFromSlider = this.updateMeasurementFromSlider.bind(this);
    this.resetMeasurementsToOriginal = this.resetMeasurementsToOriginal.bind(this);
    this.setMeasurementUnit = this.setMeasurementUnit.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Cache DOM elements
    this.modal = this.querySelector('.size-selector-modal');
    this.backdrop = this.querySelector('.modal-backdrop');
    this.closeBtn = this.querySelector('.modal-close');
    this.backBtn = this.querySelector('.modal-back');
    this.tabButtons = this.querySelectorAll('.tablinks');
    this.dropdownButton = this.querySelector('.select-dropdown__button');
    this.dropdownList = this.querySelector('.select-dropdown__list');

    // Get gender, customer ID, and long sleeve flag from data attributes
    const genderAttr = this.getAttribute('data-gender');
    if (genderAttr) {
      this.state.gender = genderAttr;
    }

    const customerIdAttr = this.getAttribute('data-customer-id');
    if (customerIdAttr) {
      this.state.customerId = customerIdAttr;
    }

    // Check if this is a long sleeve product
    const isLongSleeveAttr = this.getAttribute('data-is-long-sleeve');
    this.state.isLongSleeve = isLongSleeveAttr === 'true';

    // Check if this is a sweatshirt (for plus size fee calculation)
    const isSweatshirtAttr = this.getAttribute('data-is-sweatshirt');
    this.state.isSweatshirt = isSweatshirtAttr === 'true';

    // Plus size fee configuration
    this.state.plusSizeFeeVariantId = this.getAttribute('data-plus-size-fee-variant-id');
    this.state.sweatshirtPlusSizeFeeVariantId = this.getAttribute('data-sweatshirt-plus-size-fee-variant-id');
    this.state.plusSizeFeeAmount = this.getAttribute('data-plus-size-fee-amount') || '400';
    this.state.sweatshirtPlusSizeFeeAmount = this.getAttribute('data-sweatshirt-plus-size-fee-amount') || '500';

    // Attach event listeners
    this.attachEventListeners();

    // Initialize tabs
    this.initializeTabs();

    // Initialize sections
    this.initializeSections();

    // Register trigger buttons
    this.registerTriggers();

    // Load saved sizes if customer is logged in
    if (this.state.customerId) {
      this.loadSavedSizes();
    }
  }

  attachEventListeners() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this.close);
    }

    // Back button
    if (this.backBtn) {
      this.backBtn.addEventListener('click', this.handleReturn);
    }

    // Backdrop click
    if (this.backdrop) {
      this.backdrop.addEventListener('click', this.handleBackdropClick);
    }

    // Escape key
    document.addEventListener('keydown', this.handleEscapeKey);

    // Listen for variant changes to re-register trigger buttons
    if (typeof eventBus !== 'undefined') {
      eventBus.on('variant:change', this.onVariantChange);
    }
  }

  registerTriggers() {
    // Find all buttons with data-open-size-modal attribute
    const triggers = document.querySelectorAll('[data-open-size-modal]');

    console.log('[SizeSelectorModal] Registering triggers, found:', triggers.length);

    triggers.forEach(trigger => {
      // Remove any existing listener first to avoid duplicates
      // We'll use a named function stored on the element to track it
      if (trigger._sizeSelectorClickHandler) {
        trigger.removeEventListener('click', trigger._sizeSelectorClickHandler);
      }

      // Create and store the handler
      trigger._sizeSelectorClickHandler = (e) => {
        e.preventDefault();
        const variantId = trigger.getAttribute('data-variant-id');
        console.log('[SizeSelectorModal] Button clicked, variant ID:', variantId);
        this.open(variantId);
      };

      // Attach the handler
      trigger.addEventListener('click', trigger._sizeSelectorClickHandler);
    });
  }

  initializeSections() {
    // Set up tab navigation for I'm New section
    this.initializeSizeTypeTabs();

    // Set up size/measurement selection
    this.initializeSizeSelection();

    // Set up body type/shape selection
    this.initializeBodyTypeSelection();

    // Set up height selection
    this.initializeHeightSelection();

    // Set up fit selection
    this.initializeFitSelection();

    // Set up Create Size button
    this.initializeCreateButton();
  }

  initializeSizeTypeTabs() {
    // NOTE: Size/Measurement tabs have been unified into a single grid
    // This function is kept for backwards compatibility but no longer needed
    // Each size option now shows both size and measurement (e.g., "M / 40")
  }

  initializeSizeSelection() {
    // Unified size selection (now includes both size and measurement in data attribute)
    const sizeInputs = this.querySelectorAll('input[name="size"]');
    sizeInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        this.state.selectedSize = e.target.value;
        // Also capture the corresponding measurement from data attribute
        this.state.selectedMeasurement = e.target.dataset.measurement || null;
        console.log('Selected size:', this.state.selectedSize, '/ measurement:', this.state.selectedMeasurement);
        this.updatePlusSizeDisclaimer();
      });
    });

    // Initialize with default checked value
    const defaultChecked = this.querySelector('input[name="size"]:checked');
    if (defaultChecked) {
      this.state.selectedSize = defaultChecked.value;
      this.state.selectedMeasurement = defaultChecked.dataset.measurement || null;
    }

    // Initialize disclaimer visibility based on default selection
    this.updatePlusSizeDisclaimer();
  }

  /**
   * Check if a size is a plus size (4XL, 5XL, 6XL or 50", 52", 54")
   */
  isPlusSize(size) {
    if (!size) return false;
    const plusSizes = ['4XL', '5XL', '6XL', '50', '52', '54'];
    return plusSizes.some(ps => size.toString().includes(ps));
  }

  /**
   * Update plus size disclaimer visibility and amount
   */
  updatePlusSizeDisclaimer() {
    const disclaimer = this.querySelector('.plus-size-disclaimer');
    if (!disclaimer) return;

    const currentSize = this.state.selectedSize || this.state.selectedMeasurement;
    const isPlus = this.isPlusSize(currentSize);

    if (isPlus) {
      // Show disclaimer with correct fee amount
      const feeAmount = this.state.isSweatshirt
        ? this.state.sweatshirtPlusSizeFeeAmount
        : this.state.plusSizeFeeAmount;

      const feeAmountEl = disclaimer.querySelector('.plus-size-fee-amount');
      if (feeAmountEl) {
        feeAmountEl.textContent = `₹${feeAmount}`;
      }

      disclaimer.style.display = 'block';
      console.log('[PlusSize] Showing disclaimer, fee:', feeAmount);
    } else {
      disclaimer.style.display = 'none';
    }
  }

  initializeBodyTypeSelection() {
    if (this.state.gender === 'male') {
      // Male body type selection
      const bodyTypeInputs = this.querySelectorAll('input[name="belly"]');
      bodyTypeInputs.forEach(input => {
        const parent = input.closest('.body-type-option');

        input.addEventListener('change', (e) => {
          this.state.selectedBodyType = e.target.value;
          console.log('Selected body type:', this.state.selectedBodyType);

          // Update visual selection
          this.querySelectorAll('.body-type-option').forEach(opt => {
            opt.classList.remove('selected');
          });
          parent.classList.add('selected');
        });

        // Allow clicking on parent div
        if (parent) {
          parent.addEventListener('click', () => {
            input.checked = true;
            input.dispatchEvent(new Event('change'));
          });
        }
      });
    } else if (this.state.gender === 'female') {
      // Female shape selection
      const shapeInputs = this.querySelectorAll('input[name="shape"]');
      shapeInputs.forEach(input => {
        const parent = input.closest('.body-type-option');

        input.addEventListener('change', (e) => {
          this.state.selectedShape = e.target.value;
          console.log('Selected shape:', this.state.selectedShape);

          // Update visual selection
          this.querySelectorAll('.body-type-option').forEach(opt => {
            opt.classList.remove('selected');
          });
          parent.classList.add('selected');
        });

        if (parent) {
          parent.addEventListener('click', () => {
            input.checked = true;
            input.dispatchEvent(new Event('change'));
          });
        }
      });

      // Female bust selection
      const bustInputs = this.querySelectorAll('input[name="bust"]');
      bustInputs.forEach(input => {
        const parent = input.closest('.body-type-option');

        input.addEventListener('change', (e) => {
          this.state.selectedBust = e.target.value;
          console.log('Selected bust:', this.state.selectedBust);

          if (parent) {
            this.querySelectorAll('.chest-option').forEach(opt => {
              opt.classList.remove('selected');
            });
            parent.classList.add('selected');
          }
        });

        if (parent) {
          parent.addEventListener('click', () => {
            input.checked = true;
            input.dispatchEvent(new Event('change'));
          });
        }
      });
    }
  }

  initializeHeightSelection() {
    const heightInputs = this.querySelectorAll('input[name="height"]');
    heightInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        this.state.selectedHeight = e.target.value;
        console.log('Selected height:', this.state.selectedHeight);
      });
    });
  }

  initializeFitSelection() {
    const fitInputs = this.querySelectorAll('input[name="fit"]');
    fitInputs.forEach(input => {
      const parent = input.closest('.fit-option');

      input.addEventListener('change', (e) => {
        this.state.selectedFit = e.target.value;
        console.log('Selected fit:', this.state.selectedFit);

        // Update visual selection
        this.querySelectorAll('.fit-option').forEach(opt => {
          opt.classList.remove('selected');
        });
        parent.classList.add('selected');
      });

      // Allow clicking on parent div
      if (parent) {
        parent.addEventListener('click', () => {
          input.checked = true;
          input.dispatchEvent(new Event('change'));
        });
      }
    });
  }

  initializeCreateButton() {
    const createBtn = this.querySelector('[data-create-size]');
    if (createBtn) {
      createBtn.addEventListener('click', () => {
        this.createSize();
      });
    }
  }

  initializeTabs() {
    // Add click listeners to tab buttons
    this.tabButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const targetTab = button.getAttribute('data-tab-target');
        this.switchTab(targetTab);
      });
    });

    // Initialize dropdown functionality
    if (this.dropdownButton && this.dropdownList) {
      this.dropdownButton.addEventListener('click', () => {
        this.dropdownList.classList.toggle('active');
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.select-dropdown')) {
          this.dropdownList?.classList.remove('active');
        }
      });
    }
  }

  switchTab(tabName) {
    console.log('Switching to tab:', tabName);
    this.state.currentTab = tabName;

    // Update tab button states
    this.tabButtons.forEach(button => {
      const targetTab = button.getAttribute('data-tab-target');
      if (targetTab === tabName) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });

    // Hide ALL sections first
    const sections = this.querySelectorAll('.modal-section');
    sections.forEach(section => {
      section.classList.remove('active');
    });

    // Show only the section for the selected tab
    sections.forEach(section => {
      const tabSection = section.getAttribute('data-tab-section');
      if (tabSection === tabName) {
        section.classList.add('active');
      }
    });

    // Update current section state
    if (tabName === 'im-new') {
      this.state.currentSection = 'section-1';
    } else if (tabName === 'shopped-before') {
      this.state.currentSection = 'section-3';
    }
  }

  async loadSavedSizes() {
    console.log('Loading saved sizes for customer:', this.state.customerId);

    // Check if getData function exists (from service-configure.liquid)
    if (typeof getData !== 'function') {
      console.error('getData function not available. Make sure service-configure is included.');
      return;
    }

    try {
      const endpoint = `customer/v2/${this.state.customerId}/measurement`;
      const response = await getData(endpoint);

      console.log('Saved sizes response:', response);

      if (response?.data) {
        this.state.savedSizes = this.mapCustomerData(response.data);
        this.populateDropdown();
      }
    } catch (error) {
      console.error('Error loading saved sizes:', error);
    }
  }

  mapCustomerData(data) {
    // Map API response to usable format
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map(item => {
      const rawBodyType = item.bodyType || item.body_type;

      // Parse bodyType field:
      // - If contains "|", it's composite format for female: "silhouetteShape|bustProfile"
      // - Otherwise, it's simple format for male: "Light Belly", "Heavy Belly", etc.
      let bodyType = null;
      let silhouetteShape = null;
      let bustProfile = null;

      if (rawBodyType && rawBodyType.includes('|')) {
        // Female composite format: parse into separate fields
        const parts = rawBodyType.split('|');
        silhouetteShape = parts[0] || null;
        bustProfile = parts[1] || null;
        console.log('📋 Parsed composite bodyType:', { rawBodyType, silhouetteShape, bustProfile });
      } else {
        // Male simple format: use as-is
        bodyType = rawBodyType;
        console.log('📋 Simple bodyType:', bodyType);
      }

      return {
        id: item.id || item.customerMeasurementId,
        name: item.name,
        size: item.size,
        bodyType: bodyType,
        silhouetteShape: silhouetteShape,
        bustProfile: bustProfile,
        height: item.height,
        fit: item.fit,
        createdDate: item.createdDate || item.created_date,
        measurements: {
          shoulderWidth: item.shoulderWidth || item.shoulder_width,
          halfChestWidth: item.halfChestWidth || item.half_chest_width,
          halfBottomWidth: item.halfBottomWidth || item.half_bottom_width,
          neckWidth: item.neckWidth || item.neck_width,
          frontNeckDrop: item.frontNeckDrop || item.front_neck_drop,
          frontVNeckDrop: item.frontVNeckDrop || item.front_v_neck_drop,
          cbLength: item.cbLength || item.cb_length,
          sleeveLength: item.sleeveLength || item.sleeve_length,
          sleeveOpening: item.sleeveOpening || item.sleeve_opening,
          armholeStraight: item.armholeStraight || item.armhole_straight
        }
      };
    });
  }

  populateDropdown() {
    if (!this.dropdownList) return;

    // Clear existing items
    this.dropdownList.innerHTML = '';

    // Add each saved size as a dropdown item
    this.state.savedSizes.forEach((size, index) => {
      const li = document.createElement('li');
      li.className = 'select-dropdown__list-item';
      li.textContent = size.name;
      li.setAttribute('data-size-id', size.id);
      li.setAttribute('data-size-index', index);

      li.addEventListener('click', () => {
        this.handleSizeSelect(index);
      });

      this.dropdownList.appendChild(li);
    });
  }

  handleSizeSelect(index) {
    const selectedSize = this.state.savedSizes[index];
    if (!selectedSize) return;

    console.log('Selected saved size:', selectedSize);

    // Update UI
    const selectedSizeVal = this.querySelector('.selected-size-val');
    if (selectedSizeVal) {
      selectedSizeVal.textContent = selectedSize.name;
    }

    // Store selected size
    this.state.selectedSavedSize = selectedSize;

    // Close dropdown
    this.dropdownList?.classList.remove('active');

    // Show Edit button, hide Create New Size button
    const editBtn = this.querySelector('#editEditNewsize');
    const createBtn = this.querySelector('#createNewsize');
    if (editBtn) editBtn.style.display = 'block';
    if (createBtn) createBtn.style.display = 'none';

    // Enable Add to Bag button
    const addToBagBtn = this.querySelector('#add-to-bag-button');
    if (addToBagBtn) {
      addToBagBtn.disabled = false;
    }
  }

  calculateMeasurementsFromJSON() {
    // Check if JSON mappings are available (defined in template--product.liquid)
    if (typeof jsonMen === 'undefined' || typeof jsonWomen === 'undefined') {
      console.error('[Measurement] jsonMen or jsonWomen not found');
      return null;
    }

    // Select the correct JSON based on gender
    const selectedJson = this.state.gender === 'female' ? jsonWomen : jsonMen;
    console.log('[Measurement] Using gender:', this.state.gender);

    // Convert height from "6.6" format to height range key
    const heightRange = this.getHeightRange(this.state.selectedHeight);
    console.log('[Measurement] Height range:', heightRange, 'from height:', this.state.selectedHeight);

    // Format size to match JSON key (e.g., "6XL" → "6XL (54)")
    const sizeKey = this.formatSizeKey(this.state.selectedSize);
    console.log('[Measurement] Size key:', sizeKey);

    try {
      // Navigate through JSON structure: height → all_size → size → measurement
      const measurement = selectedJson["height"][heightRange]["all_size"][sizeKey]["measurement"];

      if (!measurement) {
        console.error('[Measurement] No measurement found for:', { heightRange, sizeKey });
        return null;
      }

      console.log('[Measurement] Found measurement:', measurement);
      console.log('[Measurement] Is long sleeve product:', this.state.isLongSleeve);

      // Use long sleeve measurements if this is a long sleeve product
      const sleeveLength = this.state.isLongSleeve
        ? parseFloat(measurement.Long_Sleeve_Length)
        : parseFloat(measurement.Sleeve_Length);
      const sleeveOpening = this.state.isLongSleeve
        ? parseFloat(measurement.Long_Sleeve_Opening)
        : parseFloat(measurement.Sleeve_Opening);

      // Convert JSON structure to our state format
      return {
        shoulderWidth: parseFloat(measurement.Shoulder_Width),
        chestWidth: parseFloat(measurement.Half_Chest_Width),
        bottomWidth: parseFloat(measurement.Bottom_Width),
        neckWidth: parseFloat(measurement.Neck_Width),
        frontNeckDrop: parseFloat(measurement.Front_Neck_Drop.Round_Neck),
        frontVNeckDrop: parseFloat(measurement.Front_Neck_Drop.V_Neck),
        cbLength: parseFloat(measurement.CB_Length),
        sleeveLength: sleeveLength,
        sleeveOpening: sleeveOpening,
        armholeStraight: parseFloat(measurement.Armhole_Straight)
      };
    } catch (error) {
      console.error('[Measurement] Error accessing JSON:', error);
      console.error('[Measurement] Attempted path:', `height[${heightRange}].all_size[${sizeKey}].measurement`);
      return null;
    }
  }

  getHeightRange(height) {
    // Convert height from "6.6" format to height range
    // Available ranges: "4.7 to 5.0", "5.1 to 5.9", "5.10 to 6.6"
    const h = parseFloat(height);

    if (h >= 4.7 && h <= 5.0) return "4.7 to 5.0";
    if (h >= 5.1 && h <= 5.9) return "5.1 to 5.9";
    if (h >= 5.10 && h <= 6.6) return "5.10 to 6.6";

    // Default to middle range if out of bounds
    console.warn('[Measurement] Height out of range:', height, 'using 5.1 to 5.9');
    return "5.1 to 5.9";
  }

  formatSizeKey(size) {
    // Convert size format to match JSON keys
    // JSON has format like: "XS (36)", "S (38)", "M (40)", "6XL (54)"
    const sizeMap = {
      'XS': 'XS (36)',
      'S': 'S (38)',
      'M': 'M (40)',
      'L': 'L (42)',
      'XL': 'XL (44)',
      'XXL': 'XXL (46)',
      '2XL': 'XXL (46)',
      '3XL': '3XL (48)',
      '4XL': '4XL (50)',
      '5XL': '5XL (52)',
      '6XL': '6XL (54)'
    };

    return sizeMap[size] || size;
  }

  createSize() {
    console.log('Creating size with state:', this.state);

    // Calculate actual measurements from JSON mappings
    const calculatedMeasurements = this.calculateMeasurementsFromJSON();

    if (calculatedMeasurements) {
      // Update state with calculated measurements
      this.state.measurements = calculatedMeasurements;
      console.log('✅ Calculated measurements:', calculatedMeasurements);
    } else {
      console.warn('⚠️ Could not calculate measurements, using defaults');
    }

    // Populate section-2 summary with current selections
    this.populateSummary();

    // Navigate to Custom Size Ready confirmation section (section-2)
    this.goToSection('section-2');
  }

  populateSummary() {
    // Populate size/measurement
    const summarySize = this.querySelector('#summary_size');
    const summaryMeasurement = this.querySelector('#summary_measurement');
    if (summarySize) summarySize.textContent = this.state.selectedSize || 'M';
    if (summaryMeasurement) summaryMeasurement.textContent = this.state.selectedMeasurement || '40';

    // Populate body type or shape (gender-specific)
    if (this.state.gender === 'male') {
      const summaryBodyType = this.querySelector('#summary_body_type');
      if (summaryBodyType) summaryBodyType.textContent = this.state.selectedBodyType;
    } else {
      const summaryShape = this.querySelector('#summary_shape');
      const summaryBust = this.querySelector('#summary_bust');
      if (summaryShape) summaryShape.textContent = this.state.selectedShape;
      if (summaryBust) summaryBust.textContent = this.state.selectedBust;
    }

    // Populate height (convert from 5.6 to 5'6")
    const summaryHeight = this.querySelector('#summary_height');
    if (summaryHeight) {
      const heightParts = this.state.selectedHeight.split('.');
      summaryHeight.textContent = `${heightParts[0]}'${heightParts[1]}"`;
    }

    // Populate fit
    const summaryFit = this.querySelector('#summary_fit');
    if (summaryFit) summaryFit.textContent = this.state.selectedFit;
  }

  async handleMeasurementDetails(payload) {
    console.log('🟣 [API] handleMeasurementDetails called');
    console.log('🟣 [API] Payload:', payload);

    // Check if postData function exists (from service-configure.liquid)
    console.log('🟣 [API] Checking if postData exists:', typeof postData);
    if (typeof postData !== 'function') {
      console.error('🔴 [API] postData function not available. Make sure service-configure is included.');
      return { success: false, error: 'API helper not available' };
    }

    const endpoint = 'customer/v2/measurement';
    console.log('🟣 [API] Endpoint:', endpoint);
    console.log('🟣 [API] Payload JSON:', JSON.stringify(payload));

    try {
      console.log('🟣 [API] Calling postData...');
      const response = await postData(endpoint, JSON.stringify(payload));
      console.log('🟣 [API] Response received:', response);

      if (response?.status?.success === "Success") {
        const customerMeasurementId = response?.data?.customerMeasurementId;
        console.log('✅ [API] Success! Customer measurement ID:', customerMeasurementId);

        // Store the measurement ID in session storage
        if (customerMeasurementId) {
          sessionStorage.setItem("singlecustomer", customerMeasurementId);
          console.log('✅ [API] Stored in session storage');
        }

        // Reload saved sizes to include the newly created one
        if (this.state.customerId) {
          console.log('🟣 [API] Reloading saved sizes...');
          await this.loadSavedSizes();
          console.log('✅ [API] Saved sizes reloaded');
        }

        return { success: true, id: customerMeasurementId };
      } else {
        // Handle duplicate name or other errors
        const errorMessage = response?.data?.customerMeasurementId || 'Failed to save size';
        console.error('🔴 [API] Failed:', errorMessage);
        console.error('🔴 [API] Full response:', response);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('🔴 [API] Exception caught:', error);
      console.error('🔴 [API] Error stack:', error.stack);
      return { success: false, error: error.message };
    }
  }

  async saveMeasurement(sizeName, variantId) {
    console.log('🟢 [Component] saveMeasurement called with:', { sizeName, variantId });
    console.log('🟢 [Component] Current state:', this.state);

    // Build the measurement payload
    // For now, use placeholder measurements since we haven't integrated Fit Algorithm API

    // Gender-aware bodyType field:
    // - Male: Use simple bodyType (e.g., "Light Belly")
    // - Female: Use composite format "silhouetteShape|bustProfile" (e.g., "Curvy Hips|Average")
    //   This is a workaround since backend doesn't support separate female fields yet
    let bodyTypeValue;
    if (this.state.gender === 'female') {
      bodyTypeValue = `${this.state.selectedShape}|${this.state.selectedBust}`;
      console.log('🟢 [Component] Female customer - using composite bodyType:', bodyTypeValue);
    } else {
      bodyTypeValue = this.state.selectedBodyType;
      console.log('🟢 [Component] Male customer - using simple bodyType:', bodyTypeValue);
    }

    const payload = {
      // User selections
      bodyType: bodyTypeValue,
      height: this.state.selectedHeight,
      fit: this.state.selectedFit,
      name: sizeName,

      // Size information - determine if using size or measurement
      size: this.state.selectedSize
        ? `${this.state.selectedSize}`
        : `${this.state.selectedMeasurement}"`,

      // Customer information
      customerEmail: this.getAttribute('data-customer-email') || '',
      customerId: this.state.customerId || '',

      // Type of measurement input
      type: this.state.selectedSize ? 'size' : 'measurement',

      // Actual measurements from JSON lookup (calculated in createSize)
      shoulderWidth: this.state.measurements.shoulderWidth?.toString() || '35',
      halfChestWidth: this.state.measurements.chestWidth?.toString() || '100',
      halfBottomWidth: this.state.measurements.bottomWidth?.toString() || '50',
      neckWidth: this.state.measurements.neckWidth?.toString() || '18',
      frontNeckDrop: this.state.measurements.frontNeckDrop?.toString() || '8',
      frontVNeckDrop: this.state.measurements.frontVNeckDrop?.toString() || '12',
      cbLength: this.state.measurements.cbLength?.toString() || '90',
      sleeveLength: this.state.measurements.sleeveLength?.toString() || '60',
      sleeveOpening: this.state.measurements.sleeveOpening?.toString() || '15',
      armholeStraight: this.state.measurements.armholeStraight?.toString() || '25'
    };

    console.log('🟢 [Component] Built payload:', payload);

    // Call the API
    console.log('🟢 [Component] Calling handleMeasurementDetails...');
    const result = await this.handleMeasurementDetails(payload);
    console.log('🟢 [Component] handleMeasurementDetails returned:', result);

    return result;
  }

  open(variantId) {
    console.log('Opening size selector modal for variant:', variantId);
    this.setAttribute('data-variant-id', variantId);
    this.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Show section 1 by default
    this.goToSection('section-1');
  }

  close() {
    console.log('Closing size selector modal');
    this.classList.remove('active');
    document.body.style.overflow = '';

    // Reset to section 1
    this.state.currentSection = 'section-1';
  }

  handleBackdropClick(e) {
    if (e.target === this.backdrop) {
      this.close();
    }
  }

  handleEscapeKey(e) {
    if (e.key === 'Escape' && this.classList.contains('active')) {
      this.close();
    }
  }

  handleReturn() {
    // If in edit mode and on section-1, exit edit mode instead of normal navigation
    if (this.state.isEditMode && this.state.currentSection === 'section-1') {
      this.exitEditMode();
      return;
    }

    // If in edit mode and on section-2 or section-4, go back to section-1 (still in edit mode)
    if (this.state.isEditMode) {
      if (this.state.currentSection === 'section-2' || this.state.currentSection === 'section-4') {
        this.goToSection('section-1');
        return;
      }
    }

    // Navigate back through sections
    const sectionMap = {
      'section-2': 'section-1',  // Custom Size Ready goes back to I'm New
      'section-3': 'section-1',  // I've Shopped Before goes back to I'm New
      'section-4': 'section-2',  // Name and Save goes back to Custom Size Ready
      'section-5': 'section-2'   // Measurement Sliders goes back to Custom Size Ready
    };

    const previousSection = sectionMap[this.state.currentSection];
    if (previousSection) {
      this.goToSection(previousSection);
    }
  }

  goToSection(sectionId) {
    console.log('Going to section:', sectionId);

    // Hide all sections
    const sections = this.querySelectorAll('.modal-section');
    sections.forEach(section => {
      section.classList.remove('active');
    });

    // Show target section
    const targetSection = this.querySelector(`[data-section="${sectionId}"]`);
    if (targetSection) {
      targetSection.classList.add('active');
      this.state.currentSection = sectionId;
    }

    // Update back button visibility
    if (this.backBtn) {
      this.backBtn.style.display = sectionId === 'section-1' ? 'none' : 'block';
    }

    // Update header title based on section
    this.updateHeaderTitle(sectionId);
  }

  updateHeaderTitle(sectionId) {
    const headerTitle = this.querySelector('.modal-header-title');
    if (!headerTitle) return;

    const titles = {
      'section-1': 'Your Size',
      'section-2': 'Your Size', // Custom Size Ready
      'section-3': 'Your Size', // I've Shopped Before
      'section-4': 'Your Size', // Name and Save
      'section-5': 'Your Product'
    };

    headerTitle.textContent = titles[sectionId] || 'Your Size';
  }

  onVariantChange(data) {
    console.log('[SizeSelectorModal] Variant changed:', data);

    // Use a small delay to ensure DOM has been updated by ProductInformation component
    // The ProductInformation component replaces innerHTML which destroys our button
    // and creates a new one, so we need to re-register event listeners
    setTimeout(() => {
      console.log('[SizeSelectorModal] Re-registering triggers after variant change');
      this.registerTriggers();
    }, 100);
  }

  /**
   * Enter edit mode for an existing saved size
   * @param {Object} savedSize - The saved size object to edit
   */
  enterEditMode(savedSize) {
    console.log('🔵 [EditMode] Entering edit mode for size:', savedSize);

    // Set edit mode state
    this.state.isEditMode = true;
    this.state.editingSizeId = savedSize.id;
    this.state.editingSizeName = savedSize.name;
    this.state.editingOriginalSize = savedSize;

    // Populate the form with saved size data
    this.populateFormWithSavedSize(savedSize);

    // Update the Create Size button to show editing state
    const createSizeBtn = this.querySelector('[data-create-size]');
    if (createSizeBtn) {
      createSizeBtn.innerHTML = `Editing: <strong>${savedSize.name}</strong>`;
    }

    // Hide the tab navigation when in edit mode
    const tabNav = this.querySelector('.tab.size-measurement');
    if (tabNav) {
      tabNav.style.display = 'none';
    }

    // Navigate to section-1 to show the form
    this.goToSection('section-1');

    // Keep the "I've Shopped Before" tab visually active but we're showing section-1
    this.tabButtons.forEach(button => {
      const targetTab = button.getAttribute('data-tab-target');
      if (targetTab === 'shopped-before') {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });
  }

  /**
   * Exit edit mode and reset to normal state
   */
  exitEditMode() {
    console.log('🔵 [EditMode] Exiting edit mode');

    // Reset edit mode state
    this.state.isEditMode = false;
    this.state.editingSizeId = null;
    this.state.editingSizeName = null;
    this.state.editingOriginalSize = null;

    // Reset the Create Size button text
    const createSizeBtn = this.querySelector('[data-create-size]');
    if (createSizeBtn) {
      createSizeBtn.textContent = 'Create Size';
    }

    // Reset section-4 UI to defaults
    const heading = document.getElementById('section4-heading');
    const subheading = document.getElementById('section4-subheading');
    const saveBtn = document.getElementById('save-size-btn');
    const sizeNameInput = document.getElementById('sizename_create');

    if (heading) heading.textContent = 'Name and Save Your Size';
    if (subheading) subheading.textContent = 'Effortlessly shop your perfect fit every time';
    if (saveBtn) saveBtn.textContent = 'Save Size';
    if (sizeNameInput) sizeNameInput.value = '';

    // Show the tab navigation again
    const tabNav = this.querySelector('.tab.size-measurement');
    if (tabNav) {
      tabNav.style.display = '';
    }

    // Navigate back to section-3 (I've Shopped Before)
    this.goToSection('section-3');

    // Re-sync the tab state
    this.state.currentTab = 'shopped-before';
    this.tabButtons.forEach(button => {
      const targetTab = button.getAttribute('data-tab-target');
      if (targetTab === 'shopped-before') {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });
  }

  /**
   * Populate form fields with saved size data
   * @param {Object} savedSize - The saved size object
   */
  populateFormWithSavedSize(savedSize) {
    console.log('🔵 [EditMode] Populating form with saved size:', savedSize);

    // Parse the size value - could be "M" or "40""
    const sizeValue = savedSize.size;
    let sizeKey = sizeValue;
    let measurementKey = null;

    if (sizeValue && sizeValue.includes('"')) {
      // It's a measurement like "40"" - find the corresponding size
      measurementKey = sizeValue.replace('"', '');
      // Find the size input that has this measurement
      const sizeInput = this.querySelector(`input[name="size"][data-measurement="${measurementKey}"]`);
      if (sizeInput) {
        sizeInput.checked = true;
        this.state.selectedSize = sizeInput.value;
        this.state.selectedMeasurement = measurementKey;
      }
    } else if (sizeKey) {
      // It's a size like "M" - select it directly
      const sizeInput = this.querySelector(`input[name="size"][value="${sizeKey}"]`);
      if (sizeInput) {
        sizeInput.checked = true;
        this.state.selectedSize = sizeKey;
        this.state.selectedMeasurement = sizeInput.dataset.measurement || null;
      }
    }

    // Update plus size disclaimer after selection
    this.updatePlusSizeDisclaimer();

    // Select body type (male) or bust profile (female)
    if (this.state.gender === 'male' && savedSize.bodyType) {
      const bodyTypeInput = this.querySelector(`input[name="belly"][value="${savedSize.bodyType}"]`);
      if (bodyTypeInput) {
        bodyTypeInput.checked = true;
        this.state.selectedBodyType = savedSize.bodyType;
        // Update visual selection
        this.querySelectorAll('.belly-option').forEach(opt => opt.classList.remove('selected'));
        const parent = bodyTypeInput.closest('.body-type-option');
        if (parent) parent.classList.add('selected');
      }
    } else if (this.state.gender === 'female') {
      // For female, check if we have bustProfile from parsed composite format
      const bustValue = savedSize.bustProfile || savedSize.bodyType;
      if (bustValue) {
        const bustInput = this.querySelector(`input[name="bust"][value="${bustValue}"]`);
        if (bustInput) {
          bustInput.checked = true;
          this.state.selectedBust = bustValue;
          // Update visual selection
          this.querySelectorAll('.chest-option').forEach(opt => opt.classList.remove('selected'));
          const parent = bustInput.closest('.body-type-option');
          if (parent) parent.classList.add('selected');
        }
      }
    }

    // Select height
    if (savedSize.height) {
      const heightInput = this.querySelector(`input[name="height"][value="${savedSize.height}"]`);
      if (heightInput) {
        heightInput.checked = true;
        this.state.selectedHeight = savedSize.height;
      }
    }

    // Select fit
    if (savedSize.fit) {
      const fitInput = this.querySelector(`input[name="fit"][value="${savedSize.fit}"]`);
      if (fitInput) {
        fitInput.checked = true;
        this.state.selectedFit = savedSize.fit;
        // Update visual selection
        this.querySelectorAll('.fit-option').forEach(opt => opt.classList.remove('selected'));
        const parent = fitInput.closest('.fit-option');
        if (parent) parent.classList.add('selected');
      }
    }

    // Store the measurements from the saved size
    if (savedSize.measurements) {
      this.state.measurements = { ...savedSize.measurements };
    }
  }

  /**
   * Update an existing measurement via PUT API
   * @param {string} sizeName - The name for the size
   * @param {string} variantId - The variant ID
   * @returns {Object} Result with success status
   */
  async updateMeasurement(sizeName, variantId) {
    console.log('🔵 [EditMode] Updating measurement:', { sizeName, variantId, id: this.state.editingSizeId });

    if (!this.state.editingSizeId) {
      console.error('🔴 [EditMode] No editing size ID found');
      return { success: false, error: 'No size ID to update' };
    }

    // Check if putData function exists
    if (typeof putData !== 'function') {
      console.error('🔴 [EditMode] putData function not available');
      return { success: false, error: 'API helper not available' };
    }

    // Gender-aware bodyType field
    let bodyTypeValue;
    if (this.state.gender === 'female') {
      bodyTypeValue = `${this.state.selectedShape}|${this.state.selectedBust}`;
    } else {
      bodyTypeValue = this.state.selectedBodyType;
    }

    const payload = {
      bodyType: bodyTypeValue,
      height: this.state.selectedHeight,
      fit: this.state.selectedFit,
      name: sizeName,
      size: this.state.selectedSize
        ? `${this.state.selectedSize}`
        : `${this.state.selectedMeasurement}"`,
      customerEmail: this.getAttribute('data-customer-email') || '',
      customerId: this.state.customerId || '',
      type: this.state.selectedSize ? 'size' : 'measurement',
      shoulderWidth: this.state.measurements.shoulderWidth?.toString() || '35',
      halfChestWidth: this.state.measurements.chestWidth?.toString() || '100',
      halfBottomWidth: this.state.measurements.bottomWidth?.toString() || '50',
      neckWidth: this.state.measurements.neckWidth?.toString() || '18',
      frontNeckDrop: this.state.measurements.frontNeckDrop?.toString() || '8',
      frontVNeckDrop: this.state.measurements.frontVNeckDrop?.toString() || '12',
      cbLength: this.state.measurements.cbLength?.toString() || '90',
      sleeveLength: this.state.measurements.sleeveLength?.toString() || '60',
      sleeveOpening: this.state.measurements.sleeveOpening?.toString() || '15',
      armholeStraight: this.state.measurements.armholeStraight?.toString() || '25'
    };

    console.log('🔵 [EditMode] Update payload:', payload);

    try {
      const endpoint = `customer/v2/customer-measurement/${this.state.editingSizeId}`;
      console.log('🔵 [EditMode] PUT endpoint:', endpoint);

      const response = await putData(endpoint, JSON.stringify(payload));
      console.log('🔵 [EditMode] Update response:', response);

      if (response?.status?.success === "Success") {
        console.log('✅ [EditMode] Size updated successfully');

        // Reload saved sizes
        if (this.state.customerId) {
          await this.loadSavedSizes();
        }

        // Exit edit mode
        this.exitEditMode();

        return { success: true };
      } else {
        const errorMessage = response?.data?.customerMeasurementId || 'Failed to update size';
        console.error('🔴 [EditMode] Update failed:', errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('🔴 [EditMode] Update exception:', error);
      return { success: false, error: error.message };
    }
  }

  // ==========================================
  // MEASUREMENT SLIDER METHODS
  // ==========================================

  /**
   * Store original measurements before editing (for reset functionality)
   */
  storeOriginalMeasurements() {
    this.state.originalMeasurements = { ...this.state.measurements };
    console.log('🔵 [Sliders] Stored original measurements:', this.state.originalMeasurements);
  }

  /**
   * Populate slider values and diagram labels with current measurements
   */
  populateMeasurementSliders() {
    console.log('🔵 [Sliders] Populating sliders with:', this.state.measurements);

    const measurementKeys = ['chestWidth', 'shoulderWidth', 'cbLength', 'sleeveLength'];

    measurementKeys.forEach(key => {
      const value = this.state.measurements[key];
      if (value !== undefined) {
        // Convert to display unit
        const displayValue = this.convertToDisplayUnit(value);

        // Update slider
        const slider = document.getElementById(`slider-${key}`);
        if (slider) {
          slider.value = displayValue;
        }

        // Update value display
        const valueDisplay = document.getElementById(`value-${key}`);
        if (valueDisplay) {
          valueDisplay.textContent = displayValue.toFixed(1);
        }

        // Update diagram label
        const diagramLabel = document.getElementById(`diagram-${key}`);
        if (diagramLabel) {
          diagramLabel.textContent = displayValue.toFixed(1);
        }
      }
    });

    // Update unit labels
    this.updateUnitLabels();
  }

  /**
   * Update a measurement value from slider input
   * @param {string} measurement - The measurement key (e.g., 'chestWidth')
   * @param {number} displayValue - The value in the current display unit
   */
  updateMeasurementFromSlider(measurement, displayValue) {
    console.log('🔵 [Sliders] Updating', measurement, 'to', displayValue, this.state.unit);

    // Convert from display unit to storage unit (always cm)
    const cmValue = this.state.unit === 'inch' ? displayValue * 2.54 : displayValue;

    // Update state
    this.state.measurements[measurement] = cmValue;

    // Update value display (in current unit)
    const valueDisplay = document.getElementById(`value-${measurement}`);
    if (valueDisplay) {
      valueDisplay.textContent = displayValue.toFixed(1);
    }

    // Update diagram label (in current unit)
    const diagramLabel = document.getElementById(`diagram-${measurement}`);
    if (diagramLabel) {
      diagramLabel.textContent = displayValue.toFixed(1);
    }

    console.log('🔵 [Sliders] Updated measurements state:', this.state.measurements);
  }

  /**
   * Reset measurements to algorithm-calculated original values
   */
  resetMeasurementsToOriginal() {
    console.log('🔵 [Sliders] Resetting measurements to original');

    if (this.state.originalMeasurements) {
      this.state.measurements = { ...this.state.originalMeasurements };
      this.populateMeasurementSliders();
    }
  }

  /**
   * Set the display unit (cm or inch) and update all displays
   * @param {string} unit - 'cm' or 'inch'
   */
  setMeasurementUnit(unit) {
    console.log('🔵 [Sliders] Setting unit to:', unit);

    this.state.unit = unit;

    // Re-populate sliders with converted values
    this.populateMeasurementSliders();
  }

  /**
   * Convert a cm value to the current display unit
   * @param {number} cmValue - Value in centimeters
   * @returns {number} Value in current display unit
   */
  convertToDisplayUnit(cmValue) {
    if (this.state.unit === 'inch') {
      return cmValue / 2.54;
    }
    return cmValue;
  }

  /**
   * Update all unit labels in the UI
   */
  updateUnitLabels() {
    const unitLabels = document.querySelectorAll('.measurementUnit');
    unitLabels.forEach(label => {
      label.textContent = this.state.unit;
    });
  }

  disconnectedCallback() {
    // Clean up event listeners
    document.removeEventListener('keydown', this.handleEscapeKey);

    // Remove variant change listener
    if (typeof eventBus !== 'undefined') {
      eventBus.off('variant:change', this.onVariantChange);
    }
  }
}

// Register the custom element
if (!customElements.get('size-selector-modal')) {
  customElements.define('size-selector-modal', SizeSelectorModal);
}
